/**
 * Pictures on a reply: the thumbnails of what is attached, and the control
 * that attaches more.
 *
 * A conversation that could carry a picture only in its opening message made
 * the second one useless — the answer to "which page?" is nearly always
 * another screenshot, and the reporter had to open a whole new report to send
 * one.
 *
 * Separate exports because they belong in separate places. A support message's
 * pictures stand in its text (`support-composer-editor`), one thumbnail at a
 * time; the strip of them is for a box whose text cannot place them. The paperclip
 * sits INSIDE the reply row, beside the text and the send button, which is
 * where every chat client puts it and where a hand already is.
 */

import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";
import type { DraftPicture } from "./support-draft-pictures";
import SupportPictureSources from "./support-picture-sources";
import { CAPTURE_UI_PROPS } from "./use-support-report";

/** The composer's own capture sources, handed to a reply so it offers the same. */
export interface ReplyPictureSources {
	isCapturing: boolean;
	/** Null where photographing the current page cannot mean anything. */
	onAddPicture: (() => void) | null;
	/** Absent where the browser cannot offer it. */
	onAddOtherSurface: (() => void) | null;
	/** What the capture-this-page tile can honestly promise here. */
	capturePageHint?: string;
}

interface SupportReplyPicturesProps {
	pictures: DraftPicture[];
	onEdit: (key: number) => void;
	onRemove: (key: number) => void;
	/** The bubble is a column ~360px wide; 90px thumbnails crowd it. */
	compact?: boolean;
}

/** What is going out with this reply. Nothing at all until something is. */
export const SupportReplyPictures: React.FC<SupportReplyPicturesProps> = ({
	pictures,
	onEdit,
	onRemove,
	compact = false,
}) =>
	pictures.length === 0 ? null : (
		<Stack
			direction="row"
			spacing={1}
			alignItems="flex-end"
			flexWrap="wrap"
			useFlexGap
			data-component="support-reply-pictures"
		>
			{pictures.map((picture, index) => (
				<SupportReplyPicture
					key={picture.key}
					picture={picture}
					number={index + 1}
					onEdit={onEdit}
					onRemove={onRemove}
					compact={compact}
				/>
			))}
		</Stack>
	);

interface SupportReplyPictureProps {
	picture: DraftPicture;
	/** The number a [picture N] line names it by; left out where it stands in the text itself. */
	number?: number;
	onEdit: (key: number) => void;
	onRemove: (key: number) => void;
	compact: boolean;
}

/** One picture waiting to be sent: click to mark it up, the bin to drop it. */
export const SupportReplyPicture: React.FC<SupportReplyPictureProps> = ({
	picture,
	number,
	onEdit,
	onRemove,
	compact,
}) => (
	<Box
		component="span"
		sx={{ display: "inline-block", position: "relative", lineHeight: 0 }}
		data-component="support-reply-picture"
	>
		{number !== undefined && (
			<Box
				component="span"
				sx={{
					position: "absolute",
					top: 2,
					left: 2,
					px: 0.5,
					borderRadius: 0.5,
					bgcolor: "background.paper",
					opacity: 0.9,
					lineHeight: 1.4,
					fontSize: 11,
					fontWeight: 600,
				}}
				data-component="support-reply-picture-number"
			>
				{number}
			</Box>
		)}
		<Tooltip title="Open large to mark it up">
			<ButtonBase
				onClick={() => onEdit(picture.key)}
				sx={{ display: "block", borderRadius: 1, cursor: "zoom-in" }}
			>
				<Box
					component="img"
					src={picture.url}
					alt="Picture attached to this reply"
					draggable={false}
					sx={{
						display: "block",
						height: compact ? 56 : 90,
						borderRadius: 1,
						border: 1,
						borderColor: "divider",
					}}
				/>
			</ButtonBase>
		</Tooltip>
		<Tooltip title="Remove this picture">
			<IconButton
				size="small"
				onClick={() => onRemove(picture.key)}
				data-component="support-reply-picture-remove"
				sx={{
					position: "absolute",
					top: 1,
					right: 1,
					p: 0.25,
					bgcolor: "background.paper",
					opacity: 0.85,
					"&:hover": { bgcolor: "background.paper", opacity: 1 },
				}}
			>
				<DeleteOutlineIcon sx={{ fontSize: 13 }} />
			</IconButton>
		</Tooltip>
	</Box>
);

interface SupportAttachButtonProps {
	onAdd: (blob: Blob) => void;
	/**
	 * Pull the glyph out to the container's text edge.
	 *
	 * An icon button carries its own padding, so aligning its BOX with the
	 * text above leaves the glyph indented by that padding. MUI's `edge`
	 * cancels it with a negative margin, which is what makes the paperclip
	 * line up with the first character rather than four pixels inside it.
	 */
	edge?: "start" | false;
	/** The same three sources everywhere — nobody's options are narrowed. */
	sources: ReplyPictureSources;
}

/** The paperclip, for the reply row. */
export const SupportAttachButton: React.FC<SupportAttachButtonProps> = ({
	onAdd,
	sources,
	edge = false,
}) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const close = (): void => setAnchor(null);

	return (
		<>
			<Tooltip title="Attach a picture">
				{/* No outline of its own. It sits in the composer's toolbar,
				    which is what says "control" — an extra border there read
				    as a stray little box floating under the field. Colour and
				    a hover fill carry the affordance instead, which is what
				    every chat composer does with its icons. */}
				<IconButton
					size="small"
					edge={edge}
					onClick={(event) => setAnchor(event.currentTarget)}
					data-component="support-reply-add-picture"
					sx={{
						color: "text.secondary",
						"&:hover": {
							borderColor: "primary.main",
							color: "primary.main",
						},
					}}
				>
					<AttachFileIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			{/* Marked for the capture, and it has to be marked HERE.
			    A Popover is a Portal: it renders at document.body, outside the
			    wrapper that carries CAPTURE_UI_PROPS for the rest of the
			    widget — so snapDOM had nothing to exclude and photographed the
			    three tiles onto the picture they were taking. Closing it first
			    is not enough either, since MUI fades it out over ~195ms and
			    the capture happens on the next frame. */}
			<Popover
				open={anchor !== null}
				anchorEl={anchor}
				onClose={close}
				anchorOrigin={{ vertical: "top", horizontal: "left" }}
				transformOrigin={{ vertical: "bottom", horizontal: "left" }}
				slotProps={{ paper: { ...CAPTURE_UI_PROPS } }}
				{...CAPTURE_UI_PROPS}
				data-component="support-reply-sources"
			>
				<Stack direction="row" spacing={1} sx={{ p: 1 }}>
					<SupportPictureSources
						isCapturing={sources.isCapturing}
						// Capturing sends the reporter to another page, so the
						// popover must not still be sitting over it.
						capturePageHint={sources.capturePageHint}
						onAddPicture={
							sources.onAddPicture
								? () => {
										close();
										sources.onAddPicture?.();
									}
								: null
						}
						onAddOtherSurface={
							sources.onAddOtherSurface
								? () => {
										close();
										sources.onAddOtherSurface?.();
									}
								: null
						}
						onUploadPicture={(file) => {
							close();
							onAdd(file);
						}}
					/>
				</Stack>
			</Popover>
		</>
	);
};
